'use client';

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Dictionary } from '@/i18n/dictionary';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

const STORAGE_KEY = 'fincrex-cookie-consent';

type Consent = 'accepted' | 'rejected';

const CookieConsentContext = createContext<{ openPreferences: () => void } | null>(null);

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) throw new Error('useCookieConsent must be used within CookieConsentProvider');
  return context;
}

function readConsent(): Consent | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'accepted' || value === 'rejected' ? value : null;
  } catch {
    return null;
  }
}

export function CookieConsentProvider({
  dictionary,
  children,
}: {
  dictionary: Dictionary['cookies'];
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    // localStorage is unavailable while rendering on the server, so the stored
    // choice is applied on the first client frame instead.
    const frame = requestAnimationFrame(() => {
      const consent = readConsent();
      setAnalytics(consent === 'accepted');
      setOpen(consent === null);
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  const openPreferences = useCallback(() => {
    setShowPreferences(true);
    setOpen(true);
  }, []);

  const persist = useCallback((consent: Consent) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, consent);
    } catch {
      // Storage unavailable — the choice simply is not remembered.
    }
    setAnalytics(consent === 'accepted');
    setShowPreferences(false);
    setOpen(false);
  }, []);

  return (
    <CookieConsentContext.Provider value={{ openPreferences }}>
      {children}
      {open && (
        <div
          role="dialog"
          aria-label={dictionary.prefsTitle}
          className="pointer-events-none fixed inset-x-[clamp(12px,2vw,24px)] bottom-[clamp(12px,2vw,24px)] z-55 flex"
        >
          <div className="pointer-events-auto flex w-full max-w-[520px] animate-slide-up flex-col gap-4 rounded-[18px] border border-line bg-surface p-5 shadow-[0_20px_60px_rgba(18,18,18,0.14)]">
            {showPreferences ? (
              <Preferences
                dictionary={dictionary}
                analytics={analytics}
                onToggle={() => setAnalytics((value) => !value)}
                onSave={() => persist(analytics ? 'accepted' : 'rejected')}
                onAcceptAll={() => persist('accepted')}
              />
            ) : (
              <Banner
                dictionary={dictionary}
                onAccept={() => persist('accepted')}
                onReject={() => persist('rejected')}
                onSettings={() => setShowPreferences(true)}
              />
            )}
          </div>
        </div>
      )}
    </CookieConsentContext.Provider>
  );
}

function Banner({
  dictionary,
  onAccept,
  onReject,
  onSettings,
}: {
  dictionary: Dictionary['cookies'];
  onAccept: () => void;
  onReject: () => void;
  onSettings: () => void;
}) {
  return (
    <>
      <p className="text-[15px] leading-relaxed">{dictionary.text}</p>
      <div className="flex flex-wrap gap-2">
        <Button size="md" onClick={onAccept}>
          {dictionary.accept}
        </Button>
        <Button size="md" variant="outline" onClick={onReject}>
          {dictionary.reject}
        </Button>
        <button
          type="button"
          onClick={onSettings}
          className="min-h-11 px-3 text-sm font-medium underline underline-offset-[3px] transition-colors hover:text-accent"
        >
          {dictionary.settings}
        </button>
      </div>
    </>
  );
}

function Preferences({
  dictionary,
  analytics,
  onToggle,
  onSave,
  onAcceptAll,
}: {
  dictionary: Dictionary['cookies'];
  analytics: boolean;
  onToggle: () => void;
  onSave: () => void;
  onAcceptAll: () => void;
}) {
  return (
    <>
      <p className="text-base font-medium">{dictionary.prefsTitle}</p>

      <div className="flex items-center justify-between gap-4 border-t border-line py-3">
        <span className="flex flex-col gap-0.5">
          <span className="text-[15px] font-medium">{dictionary.necessary}</span>
          <span className="text-[13px] text-muted">{dictionary.necessaryHint}</span>
        </span>
        <span aria-hidden className="relative h-6.5 w-11 shrink-0 rounded-full bg-line opacity-70">
          <span className="absolute top-[3px] right-[3px] size-5 rounded-full bg-surface" />
        </span>
      </div>

      <div className="flex items-center justify-between gap-4 border-y border-line py-3">
        <span className="flex flex-col gap-0.5">
          <span id="cookie-analytics" className="text-[15px] font-medium">
            {dictionary.analytics}
          </span>
          <span className="text-[13px] text-muted">{dictionary.analyticsHint}</span>
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={analytics}
          aria-labelledby="cookie-analytics"
          onClick={onToggle}
          className={cn(
            'relative h-6.5 w-11 shrink-0 rounded-full transition-colors duration-250',
            analytics ? 'bg-accent' : 'bg-line',
          )}
        >
          <span
            className={cn(
              'absolute top-[3px] size-5 rounded-full bg-surface transition-[left] duration-250 ease-soft',
              analytics ? 'left-[21px]' : 'left-[3px]',
            )}
          />
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button size="md" onClick={onSave}>
          {dictionary.save}
        </Button>
        <Button size="md" variant="outline" onClick={onAcceptAll}>
          {dictionary.accept}
        </Button>
      </div>
    </>
  );
}

export function CookieSettingsButton({ label }: { label: string }) {
  const { openPreferences } = useCookieConsent();

  return (
    <button
      type="button"
      onClick={openPreferences}
      className="inline-flex min-h-8 items-center text-left text-[15px] transition-colors hover:text-accent"
    >
      {label}
    </button>
  );
}
